import React, { useState, useEffect } from 'react';
import { 
  MdOutlineTimer,
  MdCheck,
  MdLockOutline,
  MdCheckCircle,
  MdClose,
  MdArrowForward
} from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog } from '@mui/material';
import Cookies from 'universal-cookie';
import PrimaryLoader from "../../components/common/PrimaryLoader";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { useOnboardingContext } from '../../context/OnboardingContext';
import { ROLE, ROLES } from '../../config';
import EmployeePendingOnboarding from './EmployeePendingOnboarding';

const cookies = new Cookies();

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { 
    cards, 
    completedCount, 
    progressPercent, 
    isLoading, 
    isUpdatingStatus,
    confirmModalData,
    promptComplete,
    confirmStatusChange,
    closeConfirmationModal,
    handleStartSetup, 
  } = useOnboardingContext();

  const isAllCompleted = cards.length > 0 && completedCount === cards.length;
  const role = cookies.get(ROLE);
  const isEmployee = role !== ROLES.COMPANY_ADMIN;


  useEffect(() => {
    if (searchParams.get("restricted") === "true") {
      setShowRestrictedModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isLoading && isAllCompleted && !isEmployee) {
      const isDismissed = sessionStorage.getItem("onboarding_success_dismissed") === "true";
      if (!isDismissed) {
        setShowSuccessModal(true);
      }
    }
  }, [isLoading, isAllCompleted, isEmployee]);

  const handleCloseRestrictedModal = () => {
    setShowRestrictedModal(false);
    searchParams.delete("restricted");
    setSearchParams(searchParams, { replace: true });
  };

  const handleCloseSuccessModal = () => {
    sessionStorage.setItem("onboarding_success_dismissed", "true");
    setShowSuccessModal(false);
  };

  const handleGoToDashboard = () => {
    sessionStorage.setItem("onboarding_success_dismissed", "true");
    setShowSuccessModal(false);
    navigate("/");
  };

  if (isEmployee) {
    return <EmployeePendingOnboarding />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#f9fafb]">
        <PrimaryLoader />
      </div>
    );
  }

  return (
    <div className="bg-[#f9fafb] min-h-screen w-full flex items-start justify-center p-4 sm:p-8 pt-8 sm:pt-12">
      <div className="bg-white border border-[#f1f5f9] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] rounded-[16px] w-full max-w-[1184px] h-auto min-h-[723.5px] p-6 lg:p-[40px] pb-10 relative box-border">
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 w-full">
          <div className="flex flex-col gap-[8px] items-start">
            <h1 className="font-bold text-[#0f172a] text-[30px] leading-[36px] m-0">
              Company Setup Progress
            </h1>
            <p className="font-normal text-[#64748b] text-[18px] leading-[28px] m-0">
              {isAllCompleted
  ? "All onboarding steps are completed. Your organization is now fully set up and ready to use the HRMS."
  : "Complete the following setup steps to start using HRMS smoothly."}
            </p>
          </div>
          {isAllCompleted && (
            <button
              onClick={handleGoToDashboard}
              className="bg-[#0052ea] hover:bg-[#0043c2] text-white font-medium text-[14px] px-5 py-2.5 rounded-[8px] transition-colors shrink-0 shadow-sm cursor-pointer"
            >
              Go to Dashboard
            </button>
          )}
        </div>

        {/* Overall Progress Bar */}
        <div className="flex h-[40px] items-center justify-between w-full mt-[50px]">
          <div className="bg-[#f1f5f9] h-[12px] rounded-full w-full max-w-[971px] relative overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full bg-[#15803d] transition-all duration-500 ease-in-out rounded-full" 
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex flex-col items-start shrink-0 ml-[10px]">
            <p className="font-semibold text-[#0052ea] text-[16px] leading-[24px] m-0 whitespace-nowrap">
              {progressPercent}% Completed
            </p>
          </div>
        </div>

        {/* Container for responsive layout */}
        <div className="w-full mt-[32px]">
          <div>
            {/* Steps Timeline (Hidden on small screens) */}
            <div className="relative h-[72px] w-full hidden xl:block">
              <div className="absolute border-[#cbd5e1] border-dashed border-t-2 h-[2px] left-[0] right-[0] top-[36px] z-0" />
              <div className="absolute inset-0 grid grid-cols-6 gap-[24px] w-full">
                {[1, 2, 3, 4, 5, 6].map((step) => {
                  const isCompleted = step <= completedCount;
                  return (
                    <div key={step} className="flex justify-center items-center">
                      <div className={`flex items-center justify-center rounded-full w-[40px] h-[40px] z-10 box-border transition-colors duration-300 ${isCompleted ? 'bg-[#1e293b] border-2 border-[#1e293b]' : 'bg-white border-2 border-[#cbd5e1]'}`}>
                        <p className={`font-bold text-[18px] leading-[28px] m-0 text-center ${isCompleted ? 'text-white' : 'text-[#64748b]'}`}>
                          {step}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-[20px] w-full mt-[32px]">
          {cards.map((card, index) => (
            <div key={card.id} className="bg-white border border-[#e2e8f0] rounded-[16px] flex flex-col items-center pt-[20px] pb-[18px] px-3.5 h-[345px] box-border relative shadow-2xs hover:shadow-sm transition justify-between">
              {/* Top: Step indicator & Required/Optional Tag */}
              <div className="w-full flex items-center justify-between px-0.5">
                <span className="text-[11px] font-bold text-[#64748b]">Step {index + 1}</span>
                {card.isRequired ? (
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Required
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Optional
                  </span>
                )}
              </div>

              {/* Middle: Icon, Title & Status */}
              <div className="flex flex-col items-center w-full">
                {/* Icon Container */}
                <div className="bg-[#eff6ff] flex items-center justify-center rounded-[12px] w-[56px] h-[56px] mb-[10px] shrink-0">
                  {card.icon}
                </div>
                
                {/* Title */}
                <h3 className="font-bold text-[#0f172a] text-[15px] text-center leading-[22px] whitespace-pre-line m-0 min-h-[44px] flex items-center justify-center">
                  {card.title}
                </h3>
                
                {/* Status Badge */}
                <div className="mt-2">
                  {card.completed ? (
                    <div className="bg-[#f0fdf4] border border-[#bbf7d0] flex gap-[6px] items-center px-[14px] py-[4px] rounded-full">
                      <MdCheck className="text-[#16a34a] text-[15px]" />
                      <p className="font-medium text-[#16a34a] text-[12px] leading-[16px] m-0">
                        Completed
                      </p>
                    </div>
                  ) : (
                    <div className="bg-[#fff7ed] border border-[#fed7aa] flex gap-[6px] items-center justify-center px-[12px] py-[4px] rounded-full">
                      <MdOutlineTimer className="text-[#c2410c] text-[13px]" />
                      <p className="font-medium text-[#c2410c] text-[12px] leading-[16px] m-0">
                        Remaining
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Bottom: Action Buttons */}
              <div className="w-full flex flex-col items-center mt-2">
                {!card.completed ? (
                  <div className="w-full flex flex-col items-center gap-1.5">
                    <button 
                      onClick={() => handleStartSetup(card.path)}
                      className="border border-[#0052ea] text-[#0052ea] hover:bg-blue-50 font-semibold text-[13px] leading-[18px] w-full h-[38px] rounded-[8px] transition-colors cursor-pointer bg-transparent flex items-center justify-center shadow-2xs"
                    >
                      Start Setup
                    </button>
                    {card.canMarkComplete ? (
                      <button
                        onClick={() => promptComplete(card)}
                        disabled={isUpdatingStatus}
                        className="text-[12px] text-[#0052ea] hover:text-[#003cb0] hover:underline font-medium flex items-center justify-center gap-1 cursor-pointer transition disabled:opacity-50 py-1"
                      >
                        <span>Mark as complete</span>
                      </button>
                    ) : (
                      <div className="h-[22px]" />
                    )}
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center gap-1.5">
                    <button
                      onClick={() => handleStartSetup(card.path)}
                      className="w-full h-[38px] border border-[#cbd5e1] text-[#334155] hover:bg-slate-50 font-medium text-[13px] rounded-[8px] transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                    >
                      <span>View Setup</span>
                      <MdArrowForward className="text-[14px] text-[#64748b]" />
                    </button>
                    {/* {card.canMarkComplete ? (
                      <button
                        onClick={() => promptIncomplete(card)}
                        disabled={isUpdatingStatus}
                        className="text-[11px] text-[#94a3b8] hover:text-[#dc2626] hover:underline font-medium transition cursor-pointer py-1"
                      >
                        Mark incomplete
                      </button>
                    ) : (
                      <div className="h-[22px]" />
                    )} */}
                    <div className="h-[22px]" />
                  </div>
                )}
              </div>
            </div>
          ))}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Completing / Uncompleting */}
      <ConfirmationModal
        isOpen={confirmModalData.isOpen}
        onClose={closeConfirmationModal}
        title={
          confirmModalData.status
            ? `Mark ${confirmModalData.card?.title.replace('\n', ' ')} as Complete?`
            : `Mark ${confirmModalData.card?.title.replace('\n', ' ')} as Incomplete?`
        }
        description={
          confirmModalData.status
            ? `Are you sure you want to mark ${confirmModalData.card?.title.replace('\n', ' ')} as complete? You can always configure its settings later from Company Settings.`
            : `Are you sure you want to mark ${confirmModalData.card?.title.replace('\n', ' ')} as incomplete? This will reset the setup completion status for this step.`
        }
        confirmButtonLabel={confirmModalData.status ? "Mark as Complete" : "Mark Incomplete"}
        onConfirm={confirmStatusChange}
        isLoading={isUpdatingStatus}
        loadingMessage="Updating status..."
      />

      {/* Access Restricted Popup Modal */}
      <Dialog 
        open={showRestrictedModal} 
        onClose={handleCloseRestrictedModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: "16px", padding: "8px" }
        }}
      >
        <div className="p-6 flex flex-col items-center text-center">
          <div className="bg-[#fef2f2] border border-[#fee2e2] rounded-full p-4 mb-4">
            <MdLockOutline className="text-[#dc2626] text-[36px]" />
          </div>
          <h2 className="text-[#0f172a] font-bold text-[22px] mb-2">
            Access Restricted
          </h2>
          <p className="text-[#64748b] text-[14px] leading-[22px] mb-6">
            You tried to access a restricted page. Please complete the required company onboarding setup steps below to unlock full platform access.
          </p>
          <button
            onClick={handleCloseRestrictedModal}
            className="bg-[#0052ea] hover:bg-[#0043c2] text-white font-semibold text-[15px] py-2.5 px-8 rounded-[8px] transition-colors cursor-pointer w-full"
          >
            Understood
          </button>
        </div>
      </Dialog>

      {/* Onboarding Completion Success Modal */}
      <Dialog 
        open={showSuccessModal} 
        onClose={handleCloseSuccessModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: "16px", padding: "8px" }
        }}
      >
        <div className="p-6 flex flex-col items-center text-center relative">
          <button
            onClick={handleCloseSuccessModal}
            className="absolute top-3 right-3 text-[#94a3b8] hover:text-[#475569] p-1 rounded-lg transition-colors cursor-pointer"
            aria-label="Close"
          >
            <MdClose className="text-[20px]" />
          </button>

          {/* Clean Top Icon */}
          <div className="bg-[#f0fdf4] border border-[#dcfce7] rounded-full p-4 mb-4 mt-1">
            <MdCheckCircle className="text-[#16a34a] text-[36px]" />
          </div>

          <h2 className="text-[#0f172a] font-bold text-[22px] mb-2">
            Setup Completed
          </h2>

<p className="text-[#64748b] text-[14px] leading-[22px] mb-6 max-w-[320px]">
  You have completed all required company onboarding steps. You can now access and use the full HRMS platform.
</p>

          <button
            onClick={handleGoToDashboard}
            className="bg-[#0052ea] hover:bg-[#0043c2] text-white font-medium text-[14px] py-2.5 px-6 rounded-[8px] transition-colors cursor-pointer w-full shadow-sm"
          >
            Go to Dashboard
          </button>
        </div>
      </Dialog>
    </div>
  );
}


